import { Problem } from "@bandstand/api/Problem";
import { signRequest } from "@bandstand/api/requestSignature";
import type { DeviceKeys } from "@bandstand/api/requestSignature";
import type { z } from "zod";

import { ShareError } from "./ShareError";

/** Where the share server is: on the VPS, or on this computer while working on the app. */
export function shareServerUrl(): string {
    return typeof location !== "undefined" && location.hostname === "localhost" ? "http://localhost:3000" : "https://share.stand.band";
}

/** Sends signed requests to the share server, and checks its answers with their schemas. */
export class ShareClient {
    constructor(
        private readonly keys: () => Promise<DeviceKeys>,
        private readonly url = shareServerUrl(),
    ) {}

    /** This device's key, as the share server knows it. */
    async deviceKey(): Promise<string> {
        return (await this.keys()).publicKey;
    }

    /** Sends a request, and answers with what came back, checked with `answer`. Without `answer`, it expects nothing back. */
    async request<T>(method: string, path: string, body?: unknown, answer?: z.ZodType<T>): Promise<T> {
        const text = body === undefined ? "" : JSON.stringify(body);
        const headers = { ...(await signRequest(await this.keys(), method, path, text)), "content-type": "application/json" };
        let response: Response;

        try {
            response = await fetch(this.url + path, { method, headers, body: body === undefined ? undefined : text });
        } catch {
            throw new ShareError(0, "The share server can't be reached. Check the internet connection.");
        }

        const json: unknown = response.status === 204 ? undefined : await response.json().catch(() => undefined);

        if (!response.ok) {
            const problem = Problem.safeParse(json);

            throw new ShareError(response.status, problem.success ? problem.data.error : "The share server didn't understand that.");
        }

        if (!answer) return undefined as T;

        const parsed = answer.safeParse(json);

        if (!parsed.success) throw new ShareError(response.status, "The share server answered something this app doesn't understand.");

        return parsed.data;
    }
}
